/**
 * SELECTION THE APPKIT WAY (spec/screens/list.md section 2 item 7, and section 7's "Apple
 * does not use a floating bar over the content for actions on a selection, and it does not
 * add checkboxes to rows").
 *
 * A click selects one row. Shift extends from the ANCHOR - the row the last plain click or
 * the last Cmd-click landed on - to the row now clicked, which is what makes a range
 * repeatable: dragging Shift up and down the list keeps rewriting the same range instead of
 * growing one that can never shrink. Cmd (or Ctrl on a machine with no Cmd) adds and
 * removes one row and moves the anchor with it.
 *
 * PURE, AND IT IS THE POINT. Every question this module answers - what a modifier means,
 * where a range starts, what a keyboard arrow does at the end of the list - is arithmetic
 * over an ordered list of ids, so it is tested without a browser and the component that
 * uses it holds nothing but the result.
 */
export interface RowSelection {
  /** In the list's own order, never in click order: it is what the detail pane reads. */
  readonly ids: readonly string[];
  /** Where a Shift range starts. `null` while nothing is selected. */
  readonly anchor: string | null;
}

export const EMPTY_ROW_SELECTION: RowSelection = { ids: [], anchor: null };

export type RowSelectMode = 'replace' | 'toggle' | 'range';

/**
 * THE MODIFIER, READ ONCE. `metaKey` is Cmd on a Mac and `ctrlKey` is its counterpart
 * everywhere else; a browser reports both, so both are asked. Shift wins over Cmd when
 * somebody holds the two, which is what Finder does.
 */
export function rowSelectMode(event: {
  readonly shiftKey: boolean;
  readonly metaKey: boolean;
  readonly ctrlKey: boolean;
}): RowSelectMode {
  if (event.shiftKey) return 'range';
  if (event.metaKey || event.ctrlKey) return 'toggle';
  return 'replace';
}

function inOrder(order: readonly string[], ids: ReadonlySet<string>): readonly string[] {
  return order.filter((id) => ids.has(id));
}

function rangeBetween(
  order: readonly string[],
  from: string,
  to: string,
): readonly string[] | null {
  const start = order.indexOf(from);
  const end = order.indexOf(to);
  if (start === -1 || end === -1) return null;
  return order.slice(Math.min(start, end), Math.max(start, end) + 1);
}

/** One click on `id`, with whatever the pointer's modifiers meant. */
export function selectRow(
  current: RowSelection,
  order: readonly string[],
  id: string,
  mode: RowSelectMode,
): RowSelection {
  if (mode === 'replace') return { ids: [id], anchor: id };

  if (mode === 'toggle') {
    const next = new Set(current.ids);
    if (next.has(id)) {
      next.delete(id);
      /**
       * REMOVING THE ANCHOR ROW LEAVES THE ANCHOR ON IT ANYWAY. A range is measured from
       * the row somebody last pointed at, and they pointed at this one; moving the anchor
       * to "whatever is still selected" would make the next Shift-click extend from a row
       * they have not touched since.
       */
      return { ids: inOrder(order, next), anchor: id };
    }
    next.add(id);
    return { ids: inOrder(order, next), anchor: id };
  }

  /**
   * A SHIFT-CLICK WITH NO ANCHOR IS A PLAIN CLICK. It happens on the first interaction
   * with a list and after Escape; treating it as a range from nowhere would either select
   * everything or nothing, and neither is what the finger asked for.
   */
  const anchor = current.anchor;
  if (anchor === null) return { ids: [id], anchor: id };
  const range = rangeBetween(order, anchor, id);
  if (range === null) return { ids: [id], anchor: id };
  return { ids: range, anchor };
}

/** Cmd-A over the rows this walk has actually loaded, which is what the list can name. */
export function selectAllRows(order: readonly string[]): RowSelection {
  const last = order.at(-1);
  return { ids: [...order], anchor: last ?? null };
}

/**
 * An arrow key. `delta` is -1 or 1; `extend` is Shift held, which grows the range from the
 * anchor rather than moving a single selection. At the ends of the list the movement stops
 * rather than wrapping - a list that wraps loses the reader's place.
 */
export function moveRowSelection(
  current: RowSelection,
  order: readonly string[],
  delta: number,
  extend: boolean,
): RowSelection {
  if (order.length === 0) return current;
  const focused = current.ids.at(delta < 0 ? 0 : -1);
  const from = focused === undefined ? -1 : order.indexOf(focused);
  const next = order[Math.min(Math.max(from + delta, 0), order.length - 1)];
  if (next === undefined) return current;
  return selectRow(current, order, next, extend ? 'range' : 'replace');
}

/**
 * WHAT SURVIVES A LIST THAT CHANGED UNDER THE SELECTION. A refetch, a filter, a page
 * appended: rows arrive and rows leave, and a selection holding an id that is no longer on
 * screen would let the detail pane say "3 leads selected" over two visible rows. Ids that
 * are gone are dropped, the order is re-read from the new list, and an anchor that left
 * goes with them.
 */
export function reconcileRowSelection(
  current: RowSelection,
  order: readonly string[],
): RowSelection {
  const present = new Set(order);
  const ids = inOrder(order, new Set(current.ids.filter((id) => present.has(id))));
  if (ids.length === current.ids.length && ids.every((id, index) => id === current.ids[index])) {
    return current.anchor === null || present.has(current.anchor) ? current : { ids, anchor: null };
  }
  return {
    ids,
    anchor: current.anchor !== null && present.has(current.anchor) ? current.anchor : null,
  };
}
